/**
 * PRODUCT_DEV-748: ルート lazy ローダの共有モジュール。
 *
 * もとは `router.tsx` に直書きしていたが、Minizo ビルドの `router.minizo.tsx` でも
 * まったく同じチャンク復旧が要るため切り出した。**複製してはいけない**:
 * 復旧のループ防止（直近試行から 10 秒）は「1 つの実装が 1 つの記録を見る」前提で、
 * 2 実装が別々に持つと片方の試行がもう片方から見えず無限リロードに落ちうる。
 */
import type React from "react";
import {
  loadRouteChunk,
  readLastRecoveryAt,
  recoverFromBrokenChunk,
  shouldAttemptChunkRecovery,
  writeLastRecoveryAt,
} from "@/lib/chunkRecovery";

/**
 * 壊れた lazy チャンクから自動復帰するルート lazy ローダ（PRODUCT_DEV-612 FB）。
 *
 * チャンクが `import()` できない実体は **Service Worker の precache 汚染**で、
 * 単なるリロード（= SW を経由する）では直らない。機構と復旧手順の詳細は
 * `@/lib/chunkRecovery` の冒頭コメントを参照。
 *
 * 起きていた症状:
 * - 遷移すると URL は変わるのに画面が変わらない（React Router のナビゲーションは
 *   startTransition に包まれるため、遷移先が解決しない間は前の画面が出たまま）
 * - そのまま操作を続けると画面が真っ白（ツリーごと落ちる）
 * - 既に読み込み済みのチャンクへの遷移だけは動く（自由記録では自動遷移できていた理由）
 * - 200 + `text/html` が返るので Network タブでは赤くならず pending にもならない
 *
 * **ループ防止**: 直近の復旧試行から 10 秒以内の再失敗は素通しして例外にする
 * （配信側が本当に壊れているときに無限リロードで固まらないように）。
 */
/**
 * 計測ログ用のチャンク名を factory のソースから取り出す。
 *
 * 呼び出し側 53 箇所に名前を書かせると必ず書き忘れ・食い違いが出る。
 * ビルド後は `()=>f(()=>import("./MeetingDetail-XXXX.js"), …)` になっているので、
 * ここから実チャンク名がそのまま取れる（調査時はこちらの方が有用）。
 */
export function chunkNameOf(factory: () => unknown): string {
  try {
    return (
      /import\(\s*[`'"]([^`'"]+)[`'"]\s*\)/.exec(String(factory))?.[1] ??
      "unknown"
    );
  } catch {
    return "unknown";
  }
}

/**
 * PRODUCT_DEV-703: `React.lazy` + `Suspense` から **react-router の `route.lazy`** へ移した。
 *
 * `React.lazy` はナビゲーションを止めない。URL とルートは即座に切り替わり、
 * チャンクが解決するまで `RootLayout` の `<Suspense fallback={<Splash />}>` が
 * **画面全体（タブバーごと）を白く**する。SW が全チャンクをプリキャッシュしている
 * （`vite.config.ts` の `globPatterns`）ので待ちの実体はネットワークではなく
 * パース/評価だが、それでも「クリックしたら一瞬真っ白」になり体感が悪い。
 *
 * `route.lazy` はチャンク解決までナビゲーション自体を保留するので、**現在の画面が
 * 出たまま**次の画面へ切り替わる。フィードバックは `NavigationProgress`（上端バー）が担う。
 *
 * 副次的な利点として、URL だけ先に進むことが無くなるため `NavigationWatchdog`
 * （committed location と `window.location` の乖離を見る）の誤検知余地も減る。
 */
// react-router へ渡すコンポーネント型（React.lazy と同じく props を問わない）
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = React.ComponentType<any>;

export function lazyWithReload(
  factory: () => Promise<{ default: AnyComponent }>,
): () => Promise<{ Component: AnyComponent }> {
  const name = chunkNameOf(factory);
  return async () => {
    try {
      const mod = await loadRouteChunk(name, factory);
      return { Component: mod.default };
    } catch (err: unknown) {
      const now = Date.now();
      if (!shouldAttemptChunkRecovery(now, readLastRecoveryAt())) throw err;
      writeLastRecoveryAt(now);
      console.warn("[chunk] failed to load route chunk, recovering once", {
        chunk: name,
        err,
      });
      void recoverFromBrokenChunk();
      // 復旧（リロード）が走るまで解決させない（エラー画面を一瞬見せないため）。
      // route.lazy では「遷移が保留のまま元の画面が残る」ことになり、
      // React.lazy 時代の「Splash のまま止まる」より無害。
      return new Promise<{ Component: AnyComponent }>(() => {});
    }
  };
}

